import { useRef, useState } from "react";
import { Alert, AlertTitle, Box, Button, IconButton, LinearProgress, Typography } from "@mui/material";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import { CloudUpload } from "@mui/icons-material";

import { useImport } from "../hooks/useImport";
import { useTemplates } from "../hooks/useTemplates";
import { useHandlePreSign } from "../../../hooks/useHandlePreSign";
import { useDisplayField } from "../../../hooks/useDisplayField";

import { FieldControl } from "../../../shared/components";

interface ImportProps {
    onClose?: () => void;
    onUploadComplete?: () => void;
    onUploadError?: (error: string) => void;
};

export const ImportDialog = ({ onClose, onUploadComplete, onUploadError }: ImportProps) => {

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dragActive, setDragActive] = useState(false);
    const [importType, setImportType] = useState<string>();

    const {
        selectedFile,
        isUploading,
        uploadError,
        handleFileSelect,
        uploadFile,
        reset,
    } = useImport({
        onSuccess: () => {
            onUploadComplete?.();
            onClose?.();
        },
        onError: (error: string) => {
            onUploadError?.(error);
        }
    });

    const handleFileChange = (file: File | null) => {
        handleFileSelect(file);
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileChange(e.dataTransfer.files[0]);
        }
    };

    const handleBrowseClick = () => {
        fileInputRef.current?.click();
    };

    const { getFilterOptions } = useDisplayField([]);
    const { data: templates, isLoading: templatesLoading, error: templatesError, isError: templatesIsError } = useTemplates();

    const { handleDownload } = useHandlePreSign({});

    const handleDownloadTemplate = () => {
        const file = templates?.find((t: any) => t.template_type === importType);
        handleDownload({
            type: file.content_type,
            name: file.template_name,
            s3_path: file.s3_path
        });
    };

    const handleCancel = () => {
        reset();
        onClose?.();
    };

    const handleSubmit = async () => {
        if (selectedFile && !isUploading && importType) {
            await uploadFile(importType);
        }
    };

    const getSimpleFileType = (fileName: string, mimeType?: string) => {
        const extension = fileName.split('.').pop()?.toLowerCase();
        if (extension === 'xlsx' || extension === 'xls') {
            return 'Excel';
        }
        if (extension === 'csv') {
            return 'CSV';
        }
        if (extension === 'pdf') {
            return 'PDF';
        }
        return 'File';
    };

    if (!selectedFile) {
        return (
            <Box sx={{ p: 3 }} display={'flex'} flexDirection={'column'} gap={2}>
                <FieldControl
                    keyName={"importType"}
                    label={"Import Type"}
                    variant={"select"}
                    value={importType}
                    options={getFilterOptions("IMPORT_TYPE").map((option: any) => ({ label: option.display_text, value: option.key }))}
                    onChange={(_key: string, value: string) => setImportType(value)}
                />
                <Box
                    onClick={handleBrowseClick}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    sx={{
                        borderRadius: 3,
                        textAlign: 'center',
                        backgroundColor: dragActive ? 'primary.lighter' : 'background.default',
                        cursor: 'pointer',
                        border: '2px dashed',
                        borderColor: dragActive ? 'primary.main' : 'divider',
                        px: 5,
                        py: 4,
                        transition: 'all 0.2s',
                        '&:hover': {
                            borderColor: 'primary.main',
                            backgroundColor: 'primary.lighter',
                        }
                    }}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        accept=".xlsx,.xls,.csv"
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileChange(file);
                        }}
                    />
                    <Box
                        sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            backgroundColor: 'primary.lighter',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto',
                        }}
                    >
                        <CloudUpload sx={{ fontSize: 40, color: 'primary.main' }} />
                    </Box>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                        {dragActive ? 'Drop file here' : 'Drop your file here'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        or click to browse from your computer
                    </Typography>
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            <strong>Supported formats:</strong> Excel (.xlsx, .xls), CSV (.csv)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            <strong>Maximum file size:</strong> 50 MB
                        </Typography>
                    </Box>
                </Box>

                {
                    !importType && (
                        <Alert
                            severity="warning"
                            sx={{
                                mt: 2,
                                borderRadius: 2,
                                '& .MuiAlert-icon': {
                                    fontSize: 24,
                                }
                            }}
                        >
                            <AlertTitle sx={{ fontWeight: 600 }}>Import Type Not Selected</AlertTitle>
                            <Typography variant="body2">
                                Please select an import type to ensure your file is processed correctly.
                            </Typography>
                        </Alert>
                    )
                }
                {
                    importType && (
                        <Alert
                            severity="info"
                            action={
                                <Button
                                    size="small"
                                    variant="text"
                                    color="inherit"
                                    startIcon={<DownloadIcon />}
                                    onClick={handleDownloadTemplate}
                                    loading={templatesLoading}
                                    disabled={templatesIsError || templatesLoading}
                                >
                                    Download
                                </Button>
                            }
                        >
                            {
                                templatesError ? (
                                    <AlertTitle sx={{ textAlign: 'start' }}>
                                        Unable to load templates
                                    </AlertTitle>
                                ) :
                                    (
                                        <>
                                            <AlertTitle sx={{ textAlign: 'start' }}>
                                                Download the Upload Template
                                            </AlertTitle>
                                            <>
                                                Click the Download button to get the pre-formatted{' '}
                                                <strong>.xlsx</strong> file with the correct column structure.
                                            </>
                                        </>
                                    )
                            }
                        </Alert>
                    )
                }
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }} display={'flex'} flexDirection={'column'} gap={2}>

            <FieldControl
                keyName={"importType"}
                label={"Import Type"}
                variant={"select"}
                value={importType}
                options={getFilterOptions("IMPORT_TYPE").map((option: any) => ({ label: option.display_text, value: option.key }))}
                onChange={(_key: string, value: string) => setImportType(value)}
            />

            <Box
                sx={{
                    border: '1px solid',
                    borderColor: isUploading ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                    backgroundColor: 'background.paper',
                    transition: 'all 0.2s',
                    boxShadow: isUploading ? '0 0 0 3px rgba(25, 118, 210, 0.08)' : 'none',
                }}
            >
                <Box
                    sx={{
                        p: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: isUploading ? 'primary.lighter' : 'transparent',
                        transition: 'all 0.2s',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                        <Box
                            sx={{
                                width: 56,
                                height: 56,
                                borderRadius: 2,
                                backgroundColor: 'primary.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <AttachFileIcon sx={{ color: 'white', fontSize: 28 }} />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                                variant="body1"
                                sx={{
                                    fontWeight: 600,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {selectedFile.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                {` • ${getSimpleFileType(selectedFile.name, selectedFile.type)}`}
                            </Typography>
                        </Box>
                    </Box>

                    {!isUploading && (
                        <IconButton
                            onClick={() => handleFileChange(null)}
                            size="small"
                            sx={{
                                color: 'error.main',
                                ml: 2,
                                '&:hover': {
                                    backgroundColor: 'error.lighter',
                                }
                            }}
                        >
                            <DeleteIcon />
                        </IconButton>
                    )}
                </Box>

                {isUploading && (
                    <Box sx={{ px: 2.5, pb: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                            <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
                                Uploading file...
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Please wait
                            </Typography>
                        </Box>
                        <LinearProgress sx={{ height: 6, borderRadius: 3 }} />
                    </Box>
                )}

            </Box>

            {uploadError && (
                <Alert
                    severity="error"
                    sx={{
                        mt: 2,
                        borderRadius: 2,
                        '& .MuiAlert-icon': {
                            fontSize: 24,
                        }
                    }}
                >
                    <AlertTitle sx={{ fontWeight: 600 }}>Upload Failed</AlertTitle>
                    <Typography variant="body2">{uploadError}</Typography>
                </Alert>
            )}

            {
                !importType && (
                    <Alert
                        severity="warning"
                        sx={{
                            mt: 2,
                            borderRadius: 2,
                            '& .MuiAlert-icon': {
                                fontSize: 24,
                            }
                        }}
                    >
                        <AlertTitle sx={{ fontWeight: 600 }}>Import Type Not Selected</AlertTitle>
                        <Typography variant="body2">
                            Please select an import type to ensure your file is processed correctly.
                        </Typography>
                    </Alert>
                )
            }

            {!isUploading && !uploadError && (
                <Alert
                    severity="success"
                    sx={{
                        mt: 2,
                        borderRadius: 2,
                        backgroundColor: 'success.lighter',
                        '& .MuiAlert-icon': {
                            fontSize: 24,
                        }
                    }}
                >
                    <AlertTitle sx={{ fontWeight: 600 }}>Ready to Import</AlertTitle>
                    <Typography variant="body2">
                        Your file is ready for import. Click the "Upload File" button to proceed.
                        You can monitor the progress in the Import History section.
                    </Typography>
                </Alert>
            )}

            <Box display={'flex'} justifyContent="flex-end" mt={3} gap={2}>
                <Button
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={isUploading}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!selectedFile || isUploading || !importType}
                >
                    {isUploading ? 'Uploading...' : 'Upload File'}
                </Button>
            </Box>

        </Box>
    );
};